import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Wallet, AlertTriangle, Clock, CheckCircle, XCircle, ChevronRight, ArrowLeftRight,
} from 'lucide-react';
import { formatInTimezone, useTimezone, PAGE } from '@kioskgaming/ui';
import AppLayout from '../../components/layout/AppLayout';
import { getShopGameProviders } from '../../services/api';
import type { ShopGameProvider } from '../../types';
import WalletsTab from './tabs/WalletsTab';
import SyncIssuesTab from './tabs/SyncIssuesTab';
import TransactionsTab from './tabs/TransactionsTab';

type TabKey = 'wallets' | 'sync-issues' | 'transactions';

const TABS: { key: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'wallets', label: 'Wallets', icon: Wallet },
  { key: 'sync-issues', label: 'Sync issues', icon: AlertTriangle },
  { key: 'transactions', label: 'Transactions', icon: ArrowLeftRight },
];

function KpiCard({
  label, value, icon, valueClass,
}: { label: string; value: string | number; icon: React.ReactNode; valueClass?: string }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <div className="flex items-center gap-1.5 text-stone-500 mb-2">
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-2xl font-bold tabular-nums tracking-tight ${valueClass ?? 'text-stone-900'}`}>
        {value}
      </p>
    </div>
  );
}

export default function GameWalletProviderDetailPage() {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const [provider, setProvider] = useState<ShopGameProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('wallets');

  useEffect(() => {
    if (!providerId) return;
    getShopGameProviders()
      .then((res) => {
        if (res.success && res.data) {
          setProvider(res.data.items.find((p) => p.id === providerId) ?? null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [providerId]);

  if (!providerId) return null;

  return (
    <AppLayout>
      <div className="space-y-5">
        {/* Hero card */}
        <div className="bg-gradient-to-br from-white to-blue-50 border border-stone-200 rounded-2xl p-6 shadow-sm">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm mb-4">
            <button
              type="button"
              onClick={() => navigate('/game-wallets')}
              className="text-blue-600 hover:text-blue-700 font-semibold transition-colors"
            >
              {PAGE.gameWallets}
            </button>
            <ChevronRight className="h-4 w-4 text-stone-400 shrink-0" />
            <span className="font-semibold text-stone-900">
              {loading ? '…' : (provider?.name ?? 'Provider')}
            </span>
          </nav>

          {/* Provider identity + description */}
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center font-black text-blue-600 text-xl shrink-0">
              {provider?.code?.slice(0, 2).toUpperCase() ?? '??'}
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight leading-tight text-stone-900">
                {loading ? '…' : (provider?.name ?? 'Provider')} wallets
              </h1>
              <p className="text-sm text-stone-500 mt-1">
                Player wallet balances and sync status for this Shop only.
              </p>
            </div>
          </div>
        </div>

        {/* KPI bar */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <KpiCard
            label="Total wallets"
            value={loading ? '…' : (provider?.totalWallets ?? '—')}
            icon={<Wallet className="h-4 w-4" />}
          />
          <KpiCard
            label="In sync"
            value={loading ? '…' : (provider?.inSync ?? '—')}
            icon={<CheckCircle className="h-4 w-4 text-emerald-600" />}
            valueClass="text-emerald-700"
          />
          <KpiCard
            label="Pending"
            value={loading ? '…' : (provider?.pending ?? '—')}
            icon={<Clock className="h-4 w-4 text-amber-500" />}
            valueClass="text-amber-700"
          />
          <KpiCard
            label="Failed"
            value={loading ? '…' : (provider?.failed ?? '—')}
            icon={<XCircle className="h-4 w-4 text-red-500" />}
            valueClass="text-red-700"
          />
          <KpiCard
            label="Last sync"
            value={
              loading ? '…'
              : provider?.lastSyncAt
                ? formatInTimezone(provider.lastSyncAt, timezone)
                : '—'
            }
            icon={<Clock className="h-4 w-4" />}
          />
        </div>

        {/* Tabs */}
        <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="flex flex-wrap gap-2 border-b border-stone-200 px-5 pt-5 pb-4">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={[
                    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-all duration-200 hover:scale-[1.03] active:scale-95',
                    isActive
                      ? 'bg-stone-900 text-white border-stone-900 shadow-sm'
                      : 'bg-white text-stone-700 border-stone-200 hover:bg-stone-50',
                  ].join(' ')}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {activeTab === 'wallets' && <WalletsTab providerId={providerId} providerName={provider?.name} />}
            {activeTab === 'sync-issues' && <SyncIssuesTab providerId={providerId} />}
            {activeTab === 'transactions' && <TransactionsTab providerId={providerId} />}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
